import React, { ReactElement } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styles from "./exportImportTransactionDetailsPage.module.scss";
import { IndexerService } from "../../services/indexer.service";
import { QueryKey } from "../../models/enums";
import { Loader, Paper } from "@mantine/core";
import { ExportImportTransaction, ExportImportType } from "../../models/Transaction";
import Button from "../../components/Button/Button.component";
import DetailsRowComponent from "../../components/detailsRow/detailsRow.component";
import ToggleUnixTime from "../../components/toggleUnixTime/ToggleUnixTime.component";
import CopyTextComponent from "../../components/copyText/CopyText.component";
import { convertFromWei, formatAddress, formatAmountWithLabel } from "../../utils/formatter";
import { calculateAmount } from "../../utils/exportImportHelper";
import TransactionInputOutput from "../transactionDetails/TransactionInputOutput.component";
import { txCChainImportExport, TxKeys } from "../transactionDetails/TransactionConfig";

const getInfoForType = (id: TxKeys): string => {
  const infoText = txCChainImportExport.find((el) => el.key === id);
  return infoText ? (infoText.info ? infoText.info : "Not found") : "Not found";
};

export default function ExportImportTransactionDetailsPage(): ReactElement | null {
  const navigate = useNavigate();
  const { txId } = useParams();

  const { data: importExportTransaction, isLoading: transactionLoading } =
    useQuery({
      queryKey: [QueryKey.importExportTransactionsList, txId],
      queryFn: async (): Promise<ExportImportTransaction | null> => {
        const response = await IndexerService.getCChainTransaction(
          txId!,
        );
        return response;
      },
      enabled: !!txId,
      staleTime: Infinity,
    });

  const { data: pChainNextId } =
    useQuery({
      queryKey: [QueryKey.transactionByInputId, txId],
      queryFn: async (): Promise<string | null> => {
        const response = await IndexerService.getTransactionByInputId(
          txId!,
        );
        return response.data[0] ?? null;
      },
      enabled: !!importExportTransaction && importExportTransaction.type === ExportImportType.EXPORT,
      staleTime: Infinity,
    });

  const goHome = () => {
    navigate("/");
  };

  const typeValue: JSX.Element =
    importExportTransaction?.type === ExportImportType.IMPORT ? (
      <span>Import to C-Chain</span>
    ) : (
      <span>Export from C-Chain</span>
    );

  return (
    <>
      {transactionLoading ? (
        <Loader size={50} style={{ paddingTop: "20%" }} />
      ) : (
        <div className={styles.txContainer}>
          <div className={styles.txHeader}>
            <h2 className={styles.pageTitle}>Transaction details</h2>
          </div>
          {/*<TransactionDetailsTable transaction={transaction} />*/}
          <Paper>
            {importExportTransaction ? (
              <>
                <DetailsRowComponent
                  label={"ID"}
                  info={getInfoForType(TxKeys.TX_ID)}
                  value={<CopyTextComponent
                    iconColor="gray"
                    iconBackgroundColor="inherit"
                    align="left"
                    text={formatAddress(importExportTransaction.tx_id, 25, true)}
                    value={importExportTransaction.tx_id}
                    weight={"bold"}
                    primaryColor={true}
                  ></CopyTextComponent>}
                ></DetailsRowComponent>

                <DetailsRowComponent
                  label={"Type"}
                  info={getInfoForType(TxKeys.TYPE)}
                  value={typeValue}
                ></DetailsRowComponent>

                <DetailsRowComponent
                  label={"Block"}
                  info={getInfoForType(TxKeys.BLOCK)}
                  value={<span>{importExportTransaction.tx_block}</span>}
                ></DetailsRowComponent>

                <DetailsRowComponent
                  label={"Timestamp"}
                  info={getInfoForType(TxKeys.TIMESTAMP)}
                  value={
                    <ToggleUnixTime
                      time={new Date(
                        importExportTransaction.timestamp * 1000,
                      ).toISOString()}
                    ></ToggleUnixTime>
                  }
                ></DetailsRowComponent>

                <DetailsRowComponent
                  label={"Amount"}
                  info={
                    importExportTransaction.type === ExportImportType.IMPORT
                      ? getInfoForType(TxKeys.CCHAIN_AMOUNT_IMPORT_TX)
                      : getInfoForType(TxKeys.CCHAIN_AMOUNT_EXPORT_TX)
                  }
                  value={
                    <>
                      {formatAmountWithLabel(
                        convertFromWei(
                          calculateAmount(importExportTransaction),
                        ),
                        false,
                        2,
                        true,
                      )}
                    </>
                  }
                ></DetailsRowComponent>

                <DetailsRowComponent
                  label={"Fee"}
                  info={getInfoForType(TxKeys.FEE)}
                  value={
                    <>
                      {formatAmountWithLabel(
                        convertFromWei(importExportTransaction.total_gas),
                        false,
                        4,
                      )}
                    </>
                  }
                ></DetailsRowComponent>

                {importExportTransaction.exportedinput_set && (
                  <>
                    <DetailsRowComponent
                      label={"Sent from"}
                      info={getInfoForType(TxKeys.FROM)}
                      value={
                        <CopyTextComponent
                          iconColor="gray"
                          iconBackgroundColor="inherit"
                          align="left"
                          text={formatAddress(
                            importExportTransaction.exportedinput_set[0].address
                              ?.address ?? "",
                            25,
                            true,
                          )}
                          value={
                            importExportTransaction.exportedinput_set[0].address
                              ?.address ?? ""
                          }
                          weight={"bold"}
                          link={`/address/${importExportTransaction.exportedinput_set[0].address?.address?.toString()}`}
                          primaryColor={false}
                        ></CopyTextComponent>
                      }
                    ></DetailsRowComponent>

                    <DetailsRowComponent
                      label={"Sent to"}
                      info={getInfoForType(TxKeys.TO)}
                      value={
                        <CopyTextComponent
                          iconColor="gray"
                          iconBackgroundColor="inherit"
                          align="left"
                          text={formatAddress(
                            importExportTransaction.exportedinput_set[0].address
                              ?.p_address ?? "",
                            25,
                            true,
                          )}
                          value={
                            importExportTransaction.exportedinput_set[0].address
                              ?.p_address ?? ""
                          }
                          weight={"bold"}
                          link={`/address/${importExportTransaction.exportedinput_set[0].address?.p_address?.toString()}`}
                          primaryColor={false}
                        ></CopyTextComponent>
                      }
                    ></DetailsRowComponent>
                  </>
                )}

                {importExportTransaction.importedoutput_set && (
                  <>
                    <DetailsRowComponent
                      label={"Sent from"}
                      info={getInfoForType(TxKeys.FROM)}
                      value={
                        <CopyTextComponent
                          iconColor="gray"
                          iconBackgroundColor="inherit"
                          align="left"
                          text={formatAddress(
                            importExportTransaction.importedoutput_set[0]
                              .address?.p_address ?? "",
                            25,
                            true,
                          )}
                          value={
                            importExportTransaction.importedoutput_set[0]
                              .address?.p_address ?? ""
                          }
                          weight={"bold"}
                          link={`/address/${importExportTransaction.importedoutput_set[0].address?.p_address?.toString()}`}
                          primaryColor={false}
                        ></CopyTextComponent>
                      }
                    ></DetailsRowComponent>
                    <DetailsRowComponent
                      label={"Sent to"}
                      info={getInfoForType(TxKeys.TO)}
                      value={
                        <CopyTextComponent
                          iconColor="gray"
                          iconBackgroundColor="inherit"
                          align="left"
                          text={formatAddress(
                            importExportTransaction.importedoutput_set[0]
                              ?.address?.address ?? "",
                            25,
                            true,
                          )}
                          value={
                            importExportTransaction.importedoutput_set[0]
                              .address?.address ?? ""
                          }
                          weight={"bold"}
                          link={`/address/${importExportTransaction.importedoutput_set[0].address?.address?.toString()}`}
                          primaryColor={false}
                        ></CopyTextComponent>
                      }
                    ></DetailsRowComponent>
                  </>
                )}

                <DetailsRowComponent
                  label={"Inputs/Outputs"}
                  info={getInfoForType(TxKeys.INPUT_OUTPUT)}
                  isInputOutput={true}
                  value={
                    <TransactionInputOutput
                      cChainInputs={
                        importExportTransaction.type === ExportImportType.IMPORT
                          ? importExportTransaction.importedinput_set ?? []
                          : importExportTransaction.exportedinput_set ?? []
                      }
                      cChainOutputs={
                        importExportTransaction.type === ExportImportType.IMPORT
                          ? importExportTransaction.importedoutput_set ?? []
                          : importExportTransaction.exportedoutput_set ?? []
                      }
                      nextId={pChainNextId}
                      cChainTxType={importExportTransaction.type}
                    ></TransactionInputOutput>
                  }
                ></DetailsRowComponent>
              </>
            ) : (
              //TODO: add as standalone component
              <div className={styles.errorLabel}>
                <span>Sorry.</span>
                <span>
                  We were unable to locate this address or transaction hash.
                </span>
                <div>
                  <Button addStyles={["invert"]} onClick={goHome}>
                    View latest blocks
                  </Button>
                </div>
              </div>
            )}
          </Paper>
        </div>
      )}
    </>
  );
}
